import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Receipt, LogOut, Settings as SettingsIcon, Building2, CalendarDays } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DialogProvider } from './context/DialogContext';
import { CaptureProvider } from './context/CaptureContext';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ExpenseList from './pages/ExpenseList';
import ExpenseForm from './pages/ExpenseForm';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import Clients from './pages/Clients';
import TripCalendar from './pages/TripCalendar';
import TripForm from './pages/TripForm';
import FloatingAddButton from './components/FloatingAddButton';
import UpdateToast from './components/UpdateToast';
import BottomNav from './components/mobile/BottomNav';
import MobileTopBar from './components/mobile/MobileTopBar';
import InstallPrompt from './components/mobile/InstallPrompt';
import OfflineSync from './components/OfflineSync';

// Full-screen task flows: on mobile these hide the tab bar and top bar and
// render their own header and sticky action bar
const FULL_SCREEN_ROUTES = [/^\/expenses\/new$/, /^\/expenses\/\d+\/edit$/, /^\/trips\/new$/, /^\/trips\/\d+\/edit$/];

function isFullScreenRoute(pathname) {
  return FULL_SCREEN_ROUTES.some(re => re.test(pathname));
}

function ProtectedRoute({ children }) {
  const { token, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppContent() {
  const { token, loading } = useAuth();

  if (loading) {
    return null;
  }

  // Show auth pages without navigation
  if (!token) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <CaptureProvider>
        <AppShell />
      </CaptureProvider>
    </Router>
  );
}

function AppShell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const fullScreen = isFullScreenRoute(pathname);

  return (
      <div className="min-h-screen bg-gray-50">
        {/* Skip to main content link for accessibility */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded">
          Skip to main content
        </a>
        
        {/* Navigation */}
        {!fullScreen && <MobileTopBar />}

        {/* Desktop navigation */}
        <nav className="hidden md:block bg-white shadow-sm" role="navigation" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex-shrink-0 flex items-center">
                  <Receipt className="h-8 w-8 text-blue-600" />
                  <span className="ml-2 text-xl font-bold text-gray-900">
                    Expense Tracker
                  </span>
                </div>
                <div className="ml-6 flex space-x-8">
                  <NavLink to="/dashboard" icon={<LayoutDashboard className="w-5 h-5" />}>
                    Dashboard
                  </NavLink>
                  <NavLink to="/expenses" icon={<Receipt className="w-5 h-5" />}>
                    Expenses
                  </NavLink>
                  <NavLink to="/trips" icon={<CalendarDays className="w-5 h-5" />}>
                    Trips
                  </NavLink>
                  <NavLink to="/clients" icon={<Building2 className="w-5 h-5" />}>
                    Clients
                  </NavLink>
                </div>
              </div>

              {/* Desktop: User menu */}
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-700">{user?.username}</span>
                <Link
                  to="/settings"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  aria-label="Settings"
                  title="Settings"
                >
                  <SettingsIcon className="w-4 h-4" />
                </Link>
                <button
                  onClick={logout}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  aria-label="Logout"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
              
            </div>
          </div>
        </nav>

        {/* Main content */}
        <main
          id="main-content"
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8 ${fullScreen ? '' : 'pb-bottom-nav'} md:pb-8`}
        >
          {!fullScreen && <InstallPrompt />}
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <ExpenseList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/new"
              element={
                <ProtectedRoute>
                  <ExpenseForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/:id/edit"
              element={
                <ProtectedRoute>
                  <ExpenseForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <Clients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trips"
              element={
                <ProtectedRoute>
                  <TripCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trips/new"
              element={
                <ProtectedRoute>
                  <TripForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trips/:id/edit"
              element={
                <ProtectedRoute>
                  <TripForm />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
            <Route path="/register" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>

        {/* Desktop: floating add button. Mobile: bottom tab bar with ＋ */}
        <FloatingAddButton />
        {!fullScreen && <BottomNav />}
        <OfflineSync />
      </div>
  );
}

function NavLink({ to, icon, children }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </Link>
  );
}

function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    // Register service worker and handle updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);

          // Check for updates on registration
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New version available
                  setWaitingWorker(newWorker);
                  setUpdateAvailable(true);
                }
              });
            }
          });

          // Also check if there's already a waiting worker
          if (registration.waiting && navigator.serviceWorker.controller) {
            setWaitingWorker(registration.waiting);
            setUpdateAvailable(true);
          }
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });

      // Handle controller change (new service worker activated)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });
    }
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      // Tell the waiting service worker to activate
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    setUpdateAvailable(false);
  };

  const handleDismissUpdate = () => {
    setUpdateAvailable(false);
  };

  return (
    <ErrorBoundary>
      <AuthProvider>
        <DialogProvider>
          <AppContent />
          {updateAvailable && (
            <UpdateToast onUpdate={handleUpdate} onDismiss={handleDismissUpdate} />
          )}
        </DialogProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
